import { useState } from 'react';

import { makeStyles, useTheme } from '@mui/styles';
import { Box } from '@mui/material';
import endpoints from '../../../../../../api/endpoints';
import { WithLoader } from '../../../../../common/loader';
import { useFetch } from '../../../../../../api/useFetch';

import ErrorBoundary from '../../../../../common/error-boundaries';
import { getCurrentMonth } from '../../../../../../constants/helpers/dates';
import Chart from './chart';
import ChartLegend from './chartLegend';
import MonthSelector from './monthSelector';
import { ChartData, ServicesCostData } from './interface';
import { prepareChartData, prepareDateRangeProps, generateYearsHistory } from './helpers';

const useStyles = makeStyles(theme => ({
  chartContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100% - ${theme.customComponents.usageSummaryMonthSelector.height})`,
    width: '100%',
    padding: theme.spacing(2, 3)
  }
}));

const monthRange = generateYearsHistory(2);
const UsageSummary = () => {
  const theme = useTheme();
  const classes = useStyles();
  const [selectedMonth, setSelectedMonth] = useState<string>(() => getCurrentMonth());
  const [chartData, setChartData] = useState<ChartData[]>([]);

  const { data, loading, error, refresh } = useFetch<ServicesCostData>({
    pathname: endpoints.servicesCost,
    options: {
      urlParams: prepareDateRangeProps(selectedMonth)
    },
    onSuccess: serviceCostData => {
      setChartData(prepareChartData(serviceCostData, theme));
    }
  });

  const handleMonthChange = (value: string) => {
    setSelectedMonth(value);
    refresh({
      urlParams: prepareDateRangeProps(value)
    });
  };

  return (
    <Box height="100%" width="100%">
      <MonthSelector monthsRange={monthRange} selectedMonth={selectedMonth} handleMonthChange={handleMonthChange} />
      <WithLoader loading={loading}>
        <Box className={classes.chartContainer}>
          <ErrorBoundary
            message={error}
            refreshHandler={() =>
              refresh({
                urlParams: prepareDateRangeProps(selectedMonth)
              })
            }
          >
            {data && <Chart data={chartData} totalLabel="Products & services" totalAmount={data.total.value} />}
            {data && <ChartLegend chartData={chartData} chartTotal={data.total.value} />}
          </ErrorBoundary>
        </Box>
      </WithLoader>
    </Box>
  );
};

export default UsageSummary;
